/**
 * [C35] The installment payment receipt (closes V-79; D-064, D-108, D-147 – D-150): the payment row's print button
 * opens the voucher window (`CustomerVoucherModal`, D-054: the dialog is always shown) with the receipt read from the
 * server (`POST /installments/payments/:id/print` with `view`: the contract as it stood after this payment, words of
 * the amount); print / PDF / WhatsApp record the issue first (a reprint is a copy, D-147). The copy prints on A4
 * (`InstallmentReceiptA4`) or 80 mm (`Voucher80`), the office default per type switchable here (D-149).
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CustomerVoucherModal } from "@faylasuf/ui";
import { InstallmentReceiptA4, Voucher80, installmentReceipt80, type InstallmentReceiptA4Props, type PrintOffice, type Voucher80Props } from "@faylasuf/print";
import type { InstallmentReceipt } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { pendingTab, useDocuments, usePrintJob, useVoucherFormat, whatsappLink } from "../../app/documents.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePrintOffice } from "../../app/printOffice.js";
import { useToast } from "../../app/toast.js";
import { printedNow } from "../customers/EntryVoucherContainer.js";
import { money } from "./format.js";

const PRINT_ID = "installment-receipt-print";

export function receiptProps(r: InstallmentReceipt, office: PrintOffice, locale: "ar" | "en", copy: boolean): InstallmentReceiptA4Props {
  const at = new Date(r.postedAt);
  const loc = locale === "ar" ? "ar-EG" : "en-GB";
  return {
    locale,
    office: { ...office, name: r.officeName || office.name },
    numberLabel: r.number ? `RCT-${r.number}` : null,
    copy,
    dateLabel: `${at.toLocaleDateString(loc, { year: "numeric", month: "2-digit", day: "2-digit", timeZone: r.timeZone })}${locale === "ar" ? " م" : ""}`,
    timeLabel: at.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: r.timeZone }),
    customerName: r.customer.name,
    accountNumber: r.customer.accountNumber,
    contractNumber: r.contractNumber,
    itemName: r.itemName,
    amountLabel: money(r.amount, r.currency),
    paidTotalLabel: money(r.paidWithDown, r.currency),
    remainingLabel: money(r.remaining, r.currency),
    boxLabel: r.boxName,
    wordsLabel: r.words || null,
    note: r.note,
    operatorCode: r.operator,
  };
}

export function ReceiptWindow({ operationId, phone, onClose }: { operationId: string; phone: string | null; onClose: () => void }) {
  const { source, t, locale } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const office = usePrintOffice();
  const docs = useDocuments();
  const job = usePrintJob();
  const [format, setFormat, copyIssued] = useVoucherFormat("installment_receipt"); // [C36] T-18: the switch is for one copy
  const [busy, setBusy] = useState(false);
  const view = useQuery({ queryKey: ["installment-receipt", operationId], queryFn: () => source.receiptPrinted(operationId, "view"), staleTime: 0, gcTime: 0, retry: false });
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));
  useEffect(() => {
    if (view.error) {
      fail(view.error);
      onClose();
    }
  }, [view.error]);

  const docOf = (r: InstallmentReceipt, copy: boolean) => {
    const p = receiptProps(r, office, locale, copy);
    return format === "80mm" ? { template: "voucher_80mm" as const, props: installmentReceipt80(p, printedNow(r.timeZone, locale)) } : { template: "installment_receipt_a4" as const, props: p };
  };
  const issue = async (channel: "print" | "pdf" | "whatsapp") => {
    setBusy(true);
    try {
      const x = await source.receiptPrinted(operationId, channel);
      return x.receipt ? { r: x.receipt, copy: x.copy } : null;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const print = async () => {
    const x = await issue("print");
    if (!x) return;
    const d = docOf(x.r, x.copy);
    job.start(d.template === "voucher_80mm" ? <Voucher80 {...(d.props as Voucher80Props)} id={PRINT_ID} /> : <InstallmentReceiptA4 {...(d.props as InstallmentReceiptA4Props)} id={PRINT_ID} />, PRINT_ID, format);
    copyIssued();
  };
  const pdfReq = (r: InstallmentReceipt, copy: boolean) => {
    const d = docOf(r, copy);
    return { template: d.template, props: d.props as unknown as Record<string, unknown>, fileName: `receipt-${r.number ?? operationId.slice(0, 8)}`, docType: "installment_receipt", ...(r.number ? { number: r.number } : {}), operationId, accountId: r.customer.accountId };
  };
  const pdf = async (share: boolean) => {
    const x = await issue("pdf");
    if (!x) return;
    setBusy(true);
    try {
      const req = pdfReq(x.r, x.copy);
      copyIssued();
      await (share ? docs.sharePdf(req) : docs.downloadPdf(req));
    } finally {
      setBusy(false);
    }
  };
  const whatsapp = async () => {
    const tab = pendingTab(); // [C35 review 9] opened inside the click
    const x = await issue("whatsapp");
    if (!x) return tab.cancel();
    const p = receiptProps(x.r, office, locale, x.copy);
    const text = t("print.receipt.whatsapp_text", { office: p.office.name, number: p.numberLabel ?? "", customer: p.customerName, contract: p.contractNumber, amount: p.amountLabel, remaining: p.remainingLabel, date: p.dateLabel });
    tab.go(whatsappLink(phone, text));
  };

  const r = view.data?.receipt;
  if (!r) return null;
  const preview = docOf(r, view.data!.copy);
  return (
    <>
      <CustomerVoucherModal title={t("print.receipt.modal_title")} busy={busy || job.busy} onClose={onClose} onWhatsApp={() => void whatsapp()} onSharePdf={() => void pdf(true)} onExportPdf={() => void pdf(false)} onPrint={() => void print()} format={format} onFormatChange={setFormat}>
        {preview.template === "voucher_80mm" ? (
          <div className="py-4 flex justify-center"><Voucher80 {...(preview.props as Voucher80Props)} id="installment-receipt-preview" /></div>
        ) : (
          <InstallmentReceiptA4 {...(preview.props as InstallmentReceiptA4Props)} id="installment-receipt-preview" />
        )}
      </CustomerVoucherModal>
      {job.portal}
    </>
  );
}
